/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useFormulaListStore } from '../../stores/formulaListStore';

export function FormulaFilters() {
  const {
    status, dosageForm, schedule, searchText, searchMode, sortBy, filterOptions,
    setStatus, setDosageForm, setSchedule, setSearchText, setSearchMode, setSortBy,
    loadFilterOptions, clearFilters,
  } = useFormulaListStore();

  // Debounced text search
  const [searchInput, setSearchInput] = useState(searchText || '');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Keep local input in sync when store is cleared externally (e.g., Clear button)
  useEffect(() => {
    setSearchInput(searchText || '');
  }, [searchText]);

  const handleSearchInput = useCallback((value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchText(value.trim() || undefined);
    }, 400);
  }, [setSearchText]);

  // Mutually exclusive checkboxes: clicking a checked one clears it (→ 'both')
  const handleModeToggle = useCallback((mode: 'ingredient' | 'formula') => {
    setSearchMode(searchMode === mode ? 'both' : mode);
  }, [searchMode, setSearchMode]);

  const handleClear = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchInput('');
    clearFilters();
  }, [clearFilters]);

  // Load filter options on mount
  useEffect(() => {
    if (!filterOptions) loadFilterOptions();
  }, [filterOptions, loadFilterOptions]);

  const hasActiveFilters =
    !!searchText ||
    !!dosageForm ||
    !!schedule ||
    status !== 'unassigned' ||
    searchMode !== 'both' ||
    sortBy !== 'default';

  return (
    <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-lg border border-gray-200 mb-4">
      {/* Status tabs */}
      <div className="flex rounded border border-gray-200 text-sm">
        {(['all', 'unassigned', 'assigned'] as const).map((s) => (
          <button
            key={s}
            data-testid={`status-tab-${s}`}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 capitalize ${
              status === s
                ? 'bg-dds-blue-500 text-white'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Dosage form */}
      <select
        aria-label="Dosage form filter"
        value={dosageForm || ''}
        onChange={(e) => setDosageForm(e.target.value || undefined)}
        className="text-sm border border-gray-200 rounded px-2 py-1.5"
      >
        <option value="">All Dosage Forms</option>
        {filterOptions?.dosageForms.map((df) => (
          <option key={df} value={df}>{df}</option>
        ))}
      </select>

      {/* Schedule */}
      <select
        aria-label="Schedule filter"
        value={schedule || ''}
        onChange={(e) => setSchedule(e.target.value || undefined)}
        className="text-sm border border-gray-200 rounded px-2 py-1.5"
      >
        <option value="">All Schedules</option>
        {filterOptions?.schedules.filter(Boolean).map((s) => (
          <option key={s!} value={s!}>{s}</option>
        ))}
      </select>

      {/* Combined search: text field + stacked scope checkboxes.
          Text input height is sized to match the stacked checkboxes so both
          sit flush. The two checkboxes are mutually exclusive — if none
          is checked, search matches both formula name and ingredient name. */}
      <div className="flex items-stretch gap-2">
        <div className="relative">
          <input
            type="text"
            data-testid="search-input"
            value={searchInput}
            onChange={(e) => handleSearchInput(e.target.value)}
            placeholder={
              searchMode === 'ingredient' ? 'Search by ingredient…'
              : searchMode === 'formula' ? 'Search by formula name…'
              : 'Search formula or ingredient…'
            }
            className="h-12 text-base border border-gray-200 rounded px-3 w-80 focus:outline-none focus:ring-2 focus:ring-dds-blue-500 focus:border-transparent"
          />
          {searchText && (
            <button
              onClick={() => { setSearchInput(''); setSearchText(undefined); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
              aria-label="Clear search text"
            >
              ✕
            </button>
          )}
        </div>

        <div className="flex flex-col justify-between py-0.5 text-xs text-gray-700">
          <label className="inline-flex items-center gap-1.5 cursor-pointer leading-none">
            <input
              type="checkbox"
              checked={searchMode === 'ingredient'}
              onChange={() => handleModeToggle('ingredient')}
              className="rounded border-gray-300"
            />
            <span>Ingredient Only</span>
          </label>
          <label className="inline-flex items-center gap-1.5 cursor-pointer leading-none">
            <input
              type="checkbox"
              checked={searchMode === 'formula'}
              onChange={() => handleModeToggle('formula')}
              className="rounded border-gray-300"
            />
            <span>Formula Only</span>
          </label>
        </div>
      </div>

      {/* Sort */}
      <select
        aria-label="Sort order"
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
        className="text-sm border border-gray-200 rounded px-2 py-1.5 ml-auto"
      >
        <option value="default">Sort: Unassigned first</option>
        <option value="name_asc">Name A→Z</option>
        <option value="name_desc">Name Z→A</option>
        <option value="date_desc">Newest first</option>
        <option value="source_asc">Source A→Z</option>
        <option value="source_desc">Source Z→A</option>
      </select>

      {/* Clear filters + search */}
      <button
        data-testid="clear-filters"
        onClick={handleClear}
        disabled={!hasActiveFilters}
        className="text-sm px-3 py-1.5 border border-gray-200 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Clear
      </button>
    </div>
  );
}
